import { ReportFormat } from '@cobliteam/api-lib';
import type { Preferences } from '@cobliteam/common-types';
import { useLocalStorage, useUser } from '@cobliteam/hooks';
import { Button, Chip, Dialog, Typography } from '@cobliteam/ui/components';
import Input from '@cobliteam/ui/form/Input';
import Icon from '@cobliteam/ui/icons';
import { memo, useState } from 'react';
import EmailValidator from './EmailValidator';
import ExportButton from './ExportButton';
import SuccessExportReportModal from './SuccessExportReportModal';

interface ExportModalProps {
  exportTitle: string;
  isExporting: boolean;
  availableFormats?: ReportFormat[];
  isLoadingData?: boolean;
  enableMaximumLinesTooltip?: boolean;
  disableButton?: boolean;
  onExport: (emails: string[], reportFormat: ReportFormat) => void | Promise<void>;
}

const ExportReportModal = ({
  exportTitle,
  isExporting,
  availableFormats,
  isLoadingData = false,
  disableButton = false,
  enableMaximumLinesTooltip = false,
  onExport,
}: ExportModalProps) => {
  const [localStorage, setLocalStorage] = useLocalStorage<Preferences>('preferences');

  const userEmail = useUser().getUser().email;
  const emailValidator = new EmailValidator();

  const [open, setOpen] = useState<boolean>(false);
  const [openSuccessReportModal, setOpenSuccessReportModal] = useState<boolean>(false);
  const [inputEmail, setInputEmail] = useState<string>('');
  const [emails, setEmails] = useState<string[]>(localStorage.reportsMailList || []);
  const [reportFormat, setReportFormat] = useState<ReportFormat>(ReportFormat.EXCEL);

  const inputEmailError = !emailValidator.isValid(inputEmail);

  const handleAddEmail = () => {
    const isDuplicateEmail = !!emails.find((email) => email === inputEmail) || userEmail === inputEmail;
    if (!isDuplicateEmail) {
      setEmails([...emails, inputEmail]);
      setLocalStorage({
        ...localStorage,
        reportsMailList: [...emails, inputEmail],
      });
    }

    setInputEmail('');
  };

  const handleEmailDelete = (emailRemoved: string) => {
    const newEmails = emails ? emails.filter((email) => email !== emailRemoved) : [];
    setLocalStorage({ ...localStorage, reportsMailList: newEmails });
    setEmails(newEmails);
  };

  const handleInputEmail = (event: React.ChangeEvent<HTMLInputElement>) => {
    const processedEmail = event.target.value.trim().toLowerCase();
    setInputEmail(processedEmail);
  };

  const handleEnterOnInputEmail = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !inputEmailError) handleAddEmail();
  };

  const handleExport = async () => {
    await onExport([userEmail, ...emails], reportFormat);
    setOpen(false);
    setOpenSuccessReportModal(true);
  };

  const handleDialogOpen = (format: ReportFormat) => {
    setReportFormat(format);
    setOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <ExportButton
          enableMaximumLinesTooltip={enableMaximumLinesTooltip}
          disableButton={disableButton || isLoadingData}
          onOpen={handleDialogOpen}
          availableFormats={availableFormats}
        />
        <Dialog.Content size='md' data-testid='export-report-modal'>
          <Dialog.Header>
            <Dialog.Title>{exportTitle}</Dialog.Title>
            <Dialog.Description className='sr-only' />
            <Dialog.Close />
          </Dialog.Header>

          <Dialog.Body className='mb-5'>
            <div className='mb-2 flex flex-col gap-4'>
              <Typography variant='description-list-medium' color='secondary'>
                Adicione os contatos que devem receber o relatório
              </Typography>
              <Typography variant='input-placeholder'>O relatório será enviado para o email {userEmail} e para os endereços de email da listagem abaixo.</Typography>

              <div className='flex justify-between'>
                <div className='w-3/5'>
                  <Input
                    data-testId='input-email'
                    name='email'
                    placeholder='Digite o endereço de e-mail'
                    value={inputEmail}
                    error={inputEmail.length !== 0 && inputEmailError}
                    onChange={handleInputEmail}
                    onKeyDown={handleEnterOnInputEmail}
                  />
                </div>
                <Button variant='ghost' startIcon={<Icon name='plus' />} disabled={inputEmailError} onClick={handleAddEmail}>
                  Adicionar e-mail
                </Button>
              </div>

              <Typography>E-mails cadastrados</Typography>
            </div>

            <div className='flex flex-row gap-1'>
              <Chip key={userEmail} size='sm' className='m-2' disabled>
                {userEmail}
              </Chip>
              {emails.map((email) => (
                <Chip className='m-2' size='sm' key={email} deleteIcon onDeleteClick={() => handleEmailDelete(email)}>
                  {email}
                </Chip>
              ))}
            </div>
          </Dialog.Body>

          <Dialog.Footer className='flex justify-end gap-4'>
            <Dialog.Close asChild>
              <Button variant='ghost'>Cancelar</Button>
            </Dialog.Close>

            <Button loading={isExporting} onClick={handleExport}>
              Enviar
            </Button>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog>

      <SuccessExportReportModal open={openSuccessReportModal} setOpen={setOpenSuccessReportModal} emails={[userEmail, ...emails]} />
    </>
  );
};

export default memo(ExportReportModal);
